import {
    deleteReplenishmentObjective,
    getReplenishmentPolicy,
    getReplenishmentSourcing,
    refreshReplenishmentRun,
    saveReplenishmentObjective,
    saveReplenishmentPolicy,
    saveReplenishmentSourcing,
    type LevelPolicy,
    type LevelSpec,
    type MaterialChoice,
    type ObjectiveAnchor,
    type ObjectivePeriodRule,
    type ObjectiveShape,
    type ReplenishmentPolicy,
    type RouteChoice,
    type SavedObjective,
    type SourceChoice,
    type SourcingChoices,
    type SourcingPatch,
    type SourcingRoute,
    type SourcingRule,
} from '@api/replenishment-engine';
import { getReplenishmentConfig, previewDemand, updateReplenishmentConfig, type DemandPolicy, type DemandPreview } from '@api/replenishment-planning';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { ActionIcon, Alert, Button, Group, Loader, NumberInput, RemoveScroll, SegmentedControl, Select, Stack, Stepper, Switch, TagsInput, Text, TextInput, Tooltip } from '@mantine/core';
import { ArrowRight, Trash, X } from '@phosphor-icons/react';
import { formatCount } from '@utils/currency';
import { useCallback, useEffect, useMemo, useState, type FC, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { DatePickerInput } from '@mantine/dates';

import DemandWorkbook from './DemandWorkbook';
import { dayLabel, localDateKey, monthEndKey } from './dates';
import { ANCHOR_LABEL, ANCHOR_MEANING, describePeriodRule, horizonEstimate, OBJECTIVE_TEMPLATES, parseObjectiveSentence, SHAPE_FOR_ANCHOR, SHAPE_LABEL, windowOf, sentenceFor } from './objective';
import classes from './wizard.module.css';

type Props = {
    opened: boolean;
    onClose: () => void;
    objectives: SavedObjective[];
    onObjectivesChanged: () => void;
    /** Called after "Run now" finishes, so the page reloads its tables. */
    onRan: () => void;
};

const readError = (e: unknown): string => {
    const err = e as { error?: { message?: string }; message?: string };
    return err?.error?.message ?? err?.message ?? 'Something went wrong';
};

const Field: FC<{ label: string; hint?: string; children: ReactNode }> = ({ label, hint, children }) => (
    <Stack gap={4}>
        <Text size="sm" fw={600}>
            {label}
        </Text>
        {hint && (
            <Text size="xs" c="dimmed">
                {hint}
            </Text>
        )}
        {children}
    </Stack>
);

/** A level as the brand says it: days of cover, or the SKU's own lead time plus a margin. */
const LevelEditor: FC<{ value: LevelSpec | null; fallbackDays: number; onChange: (v: LevelSpec | null) => void; allowSame?: boolean }> = ({ value, fallbackDays, onChange, allowSame }) => {
    const mode = value == null ? (allowSame ? 'SAME' : 'DAYS') : value.kind;
    return (
        <Group gap="xs" align="center" wrap="wrap">
            <SegmentedControl
                size="xs"
                value={mode}
                onChange={(m) => {
                    if (m === 'SAME') onChange(null);
                    else if (m === 'DAYS') onChange({ kind: 'DAYS', days: value?.kind === 'DAYS' ? value.days : fallbackDays });
                    else onChange({ kind: 'LEAD_TIME', plusDays: value?.kind === 'LEAD_TIME' ? (value.plusDays ?? 0) : 0 });
                }}
                data={[...(allowSame ? [{ value: 'SAME', label: 'Same as safety stock' }] : []), { value: 'DAYS', label: 'Days of cover' }, { value: 'LEAD_TIME', label: "The SKU's lead time" }]}
            />
            {value?.kind === 'DAYS' && <NumberInput size="xs" w={120} min={1} value={value.days} onChange={(v) => onChange({ kind: 'DAYS', days: Number(v) || 1 })} suffix=" days" />}
            {value?.kind === 'LEAD_TIME' && <NumberInput size="xs" w={140} min={0} value={value.plusDays ?? 0} onChange={(v) => onChange({ kind: 'LEAD_TIME', plusDays: Number(v) || 0 })} prefix="+ " suffix=" days" />}
        </Group>
    );
};

const TierRows: FC<{ policy: LevelPolicy | null; fallbackDays: number; tiers: string[]; onChange: (p: LevelPolicy) => void }> = ({ policy, fallbackDays, tiers, onChange }) => {
    const overrides = policy?.tiers ?? {};
    const brand = policy?.brand ?? { kind: 'DAYS', days: fallbackDays };
    const set = (tier: string, spec: LevelSpec | null): void => {
        const next = { ...overrides };
        if (spec) next[tier] = spec;
        else delete next[tier];
        onChange({ brand, tiers: next, skus: policy?.skus });
    };
    const all = [...new Set([...tiers, ...Object.keys(overrides)])];
    if (all.length === 0) return null;
    return (
        <Stack gap={6}>
            {all.map((tier) => (
                <Group key={tier} gap="xs" wrap="nowrap" align="center">
                    <Text size="sm" w={70}>
                        Tier {tier}
                    </Text>
                    {overrides[tier] ? (
                        <>
                            <LevelEditor value={overrides[tier]} fallbackDays={fallbackDays} onChange={(v) => set(tier, v)} />
                            <Tooltip label="Use the brand default for this tier" withArrow>
                                <ActionIcon variant="subtle" color="gray" onClick={() => set(tier, null)} aria-label="Remove override">
                                    <Trash size={14} />
                                </ActionIcon>
                            </Tooltip>
                        </>
                    ) : (
                        <Button size="compact-xs" variant="subtle" onClick={() => set(tier, { ...brand })}>
                            brand default · override
                        </Button>
                    )}
                </Group>
            ))}
        </Stack>
    );
};

const n = (v: number, d = 1): string => formatCount(v, { maxDecimals: d });

const describeLevel = (spec: LevelSpec | null | undefined, fallback: string): string => {
    if (!spec) return fallback;
    if (spec.kind === 'DAYS') return `${spec.days} days of cover`;
    return spec.plusDays ? `each SKU's longest lead + ${spec.plusDays} days` : "each SKU's longest lead time";
};

type Composer = { id?: string; sentence: string; anchor: ObjectiveAnchor; shape: ObjectiveShape; periodRule: ObjectivePeriodRule; coverDays: number | null; cues: string[]; fromTemplate: boolean };

/** The next twelve calendar months as fixed windows, for the month shortcut. */
const monthOptions = (today: string): Array<{ value: string; label: string }> => {
    const [y, m] = today.split('-').map(Number);
    return Array.from({ length: 12 }, (_, i) => {
        const d = new Date(y, m - 1 + i, 1);
        const start = localDateKey(d);
        return { value: start, label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) };
    });
};

const CHOICE_RULE_OPTIONS = [
    { value: 'SHORTEST_LEAD', label: 'Shortest lead wins' },
    { value: 'SPLIT_BY_SHARE', label: 'Split evenly across them' },
    { value: 'ASK', label: 'I choose on every order' },
];

const ROUTE_LABEL: Record<SourcingRoute, string> = { MAKE: 'Make', SFG: 'Make as SFG', BUY: 'Buy' };
const MODE_OPTIONS = [
    { value: 'PRIMARY_ONLY', label: 'Only this one' },
    { value: 'OVERFLOW', label: 'This one, spill to the next' },
    { value: 'SPLIT', label: 'Split evenly' },
];

/** A rule from a primary and a mode: the primary first, the rest in their listed order. */
const ruleFrom = (primary: string, mode: SourcingRule['mode'], optionIds: string[]): SourcingRule => {
    const order = [primary, ...optionIds.filter((id) => id !== primary)];
    return mode === 'SPLIT' ? { mode, order, ratios: Object.fromEntries(order.map((id) => [id, 1 / order.length])) } : { mode, order };
};

/** Which source and how, for one product or material with more than one. */
const SourcePicker: FC<{ options: SourceChoice['options']; current: SourcingRule | null; onChange: (r: SourcingRule) => void }> = ({ options, current, onChange }) => {
    const ids = options.map((o) => o.id);
    const primary = current?.order.find((id) => ids.includes(id)) ?? null;
    const mode = current?.mode ?? 'OVERFLOW';
    return (
        <Group gap="xs" wrap="nowrap">
            <Select size="xs" w={260} placeholder="pick one" value={primary} data={options.map((o) => ({ value: o.id, label: `${o.name} · ${o.leadDays}d${o.capacityPerDay ? ` · ${n(o.capacityPerDay, 0)}/day` : ''}` }))} onChange={(v) => v && onChange(ruleFrom(v, mode, ids))} />
            <Select size="xs" w={200} value={mode} data={MODE_OPTIONS} disabled={!primary} onChange={(v) => v && primary && onChange(ruleFrom(primary, v as SourcingRule['mode'], ids))} />
        </Group>
    );
};

/**
 * The brand's setup, as one walk through: safety stock and refill, objectives, who gets
 * scarce material, demand per channel, then run. Each step saves on Continue. Opens as a
 * full sheet over the page, not a drawer.
 */
const SetupWizard: FC<Props> = ({ opened, onClose, objectives, onObjectivesChanged, onRan }) => {
    const [step, setStep] = useState(0);
    const [policy, setPolicy] = useState<ReplenishmentPolicy | null>(null);
    const [tiers, setTiers] = useState<string[]>([]);
    const [draft, setDraft] = useState<Partial<ReplenishmentPolicy>>({});
    const [demand, setDemand] = useState<DemandPolicy | null>(null);
    const [sourcing, setSourcing] = useState<SourcingChoices | null>(null);
    const [sourcingDraft, setSourcingDraft] = useState<SourcingPatch>({});
    const [loadingSourcing, setLoadingSourcing] = useState(false);
    /** Anything saved this visit: the engine runs when the sheet closes, so the tables never lag the settings. */
    const [touched, setTouched] = useState(false);
    const [preview, setPreview] = useState<DemandPreview | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** The objective being composed: from a template or a typed sentence, shown as variables before it is added. */
    const [composer, setComposer] = useState<Composer | null>(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [p, cfg] = await Promise.all([getReplenishmentPolicy(), getReplenishmentConfig()]);
            setPolicy(p);
            setDraft({});
            setTiers(cfg.tierLabels ?? []);
            setDemand(cfg.demandPolicy ?? null);
            setSourcing(null);
            setSourcingDraft({});
            setTouched(false);
        } catch (e) {
            setError(readError(e));
        }
    }, []);
    useEffect(() => {
        if (opened) {
            setStep(0);
            void load();
        }
    }, [opened, load]);

    const refreshPreview = useCallback(async (p: DemandPolicy | null) => {
        setPreviewing(true);
        try {
            setPreview(await previewDemand(p ?? undefined));
        } catch (e) {
            setError(readError(e));
        } finally {
            setPreviewing(false);
        }
    }, []);
    const loadSourcing = useCallback(async () => {
        setLoadingSourcing(true);
        try {
            setSourcing(await getReplenishmentSourcing());
        } catch (e) {
            setError(readError(e));
        } finally {
            setLoadingSourcing(false);
        }
    }, []);
    useEffect(() => {
        if (opened && step === 1 && !sourcing) void loadSourcing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, step]);
    useEffect(() => {
        if (opened && step === 4) void refreshPreview(demand);
        // The preview follows the policy as it is edited; the policy itself is saved on Continue.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, step]);

    const eff = { ...(policy ?? {}), ...draft } as ReplenishmentPolicy;
    const fallbackDays = policy?.defaultTargetDohDays ?? 30;
    const runEvery = Math.max(1, eff.runEveryDays ?? 1);
    const brandLevel: LevelSpec = eff.levelPolicy?.brand ?? { kind: 'DAYS', days: fallbackDays };
    const dirty = Object.keys(draft).length > 0;

    const savePolicy = async (): Promise<boolean> => {
        if (!dirty) return true;
        setSaving(true);
        setError(null);
        try {
            const saved = await saveReplenishmentPolicy(draft);
            setPolicy(saved);
            setDraft({});
            setTouched(true);
            return true;
        } catch (e) {
            setError(readError(e));
            return false;
        } finally {
            setSaving(false);
        }
    };
    const saveDemand = async (): Promise<boolean> => {
        setSaving(true);
        setError(null);
        try {
            await updateReplenishmentConfig({ demandPolicy: demand ?? undefined });
            setTouched(true);
            return true;
        } catch (e) {
            setError(readError(e));
            return false;
        } finally {
            setSaving(false);
        }
    };
    const sourcingDirty = Object.keys(sourcingDraft).length > 0;
    const saveSourcing = async (): Promise<boolean> => {
        if (!sourcingDirty) return true;
        setSaving(true);
        setError(null);
        try {
            await saveReplenishmentSourcing(sourcingDraft);
            setSourcingDraft({});
            setSourcing(null);
            setTouched(true);
            return true;
        } catch (e) {
            setError(readError(e));
            return false;
        } finally {
            setSaving(false);
        }
    };
    /** Saves what the current step changed. Every step can be opened directly; leaving one saves it. */
    const saveStep = async (): Promise<boolean> => {
        if (step === 1) return (await saveSourcing()) && (await savePolicy());
        return step === 4 ? saveDemand() : savePolicy();
    };
    const next = async (): Promise<void> => {
        if (await saveStep()) setStep((s) => Math.min(5, s + 1));
    };
    const goTo = async (target: number): Promise<void> => {
        if (target === step) return;
        if (await saveStep()) setStep(target);
    };
    const runNow = async (): Promise<void> => {
        setRunning(true);
        setError(null);
        try {
            if (!(await savePolicy())) return;
            await refreshReplenishmentRun();
            setTouched(false);
            onRan();
            onClose();
        } catch (e) {
            setError(readError(e));
        } finally {
            setRunning(false);
        }
    };
    /** Closing after a change runs the engine, so a saved step is never left unreflected on the page. */
    const close = async (): Promise<void> => {
        onClose();
        if (!touched) return;
        try {
            await refreshReplenishmentRun();
            onRan();
        } catch {
            // No objective on the page yet: nothing to run for. The Run step says so.
        }
    };
    const setRoute = (productId: string, route: SourcingRoute): void => setSourcingDraft((d) => ({ ...d, routes: { ...(d.routes ?? {}), [productId]: route } }));
    const setFactory = (productId: string, rule: SourcingRule): void => setSourcingDraft((d) => ({ ...d, factories: { ...(d.factories ?? {}), [productId]: rule } }));
    const setVendor = (materialId: string, rule: SourcingRule): void => setSourcingDraft((d) => ({ ...d, vendors: { ...(d.vendors ?? {}), [materialId]: rule } }));

    useEffect(() => {
        if (!opened) return;
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') void close();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // close() is recreated each render; the listener only needs the latest on open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened]);

    const addObjective = async (o: { id?: string; sentence: string; anchor: ObjectiveAnchor; shape: ObjectiveShape; periodRule: ObjectivePeriodRule; coverDays: number | null }): Promise<void> => {
        setError(null);
        try {
            const existing = o.id ? objectives.find((x) => x.id === o.id) : undefined;
            await saveReplenishmentObjective({ id: o.id, sentence: o.sentence, anchor: o.anchor, shape: o.anchor === 'DIP' ? o.shape : SHAPE_FOR_ANCHOR[o.anchor], periodRule: o.periodRule, coverDays: o.anchor === 'MATERIAL_PLACE_BY' ? o.coverDays : null, previewLevels: existing?.previewLevels ?? null, onHome: existing?.onHome ?? true });
            setTouched(true);
            setComposer(null);
            onObjectivesChanged();
        } catch (e) {
            setError(readError(e));
        }
    };
    const composeFromSentence = (sentence: string): void => {
        if (!sentence.trim()) {
            setComposer(null);
            return;
        }
        const parsed = parseObjectiveSentence(sentence);
        setComposer({ sentence, anchor: parsed.anchor, shape: parsed.shape, periodRule: parsed.periodRule, coverDays: parsed.coverDays, cues: parsed.cues, fromTemplate: false });
    };
    const composeFromTemplate = (t: (typeof OBJECTIVE_TEMPLATES)[number]): void => setComposer({ sentence: t.sentence, anchor: t.anchor, shape: t.shape, periodRule: t.periodRule, coverDays: t.coverDays ?? null, cues: [], fromTemplate: true });
    /** A saved objective back in the composer: its variables are what is saved, so the sentence is rebuilt from them. */
    const composeFromSaved = (o: SavedObjective): void => setComposer({ id: o.id, sentence: sentenceFor(o.anchor, o.periodRule, o.coverDays, o.shape), anchor: o.anchor, shape: o.shape, periodRule: o.periodRule, coverDays: o.coverDays ?? null, cues: [], fromTemplate: false });
    // Editing a variable rewrites the sentence: the sentence is a label for the variables, never the other way round.
    // The anchor keeps the shape it was shown as when both agree on the document; a dip anchor can show any table.
    const setComposerAnchor = (anchor: ObjectiveAnchor): void =>
        setComposer((c) => {
            if (!c) return c;
            const shape = anchor === 'DIP' ? (c.shape === 'SKU' ? SHAPE_FOR_ANCHOR[c.anchor] : c.shape) : SHAPE_FOR_ANCHOR[anchor];
            return { ...c, anchor, shape, sentence: sentenceFor(anchor, c.periodRule, c.coverDays, shape), fromTemplate: false };
        });
    const setComposerShape = (shape: ObjectiveShape): void => setComposer((c) => (c ? { ...c, shape, sentence: sentenceFor(c.anchor, c.periodRule, c.coverDays, shape), fromTemplate: false } : c));
    const setComposerCover = (coverDays: number | null): void => setComposer((c) => (c ? { ...c, coverDays, sentence: sentenceFor(c.anchor, c.periodRule, coverDays, c.shape), fromTemplate: false } : c));
    const setComposerRule = (patch: Partial<{ kind: 'LEAD_TIME' | 'RELATIVE' | 'FIXED'; offsetDays: number; lengthDays: number; plusDays: number; start: string; end: string }>): void =>
        setComposer((c) => {
            if (!c) return c;
            const cur = c.periodRule;
            const kind = patch.kind ?? cur.kind;
            const today = localDateKey();
            const periodRule: ObjectivePeriodRule =
                kind === 'LEAD_TIME'
                    ? { kind: 'LEAD_TIME', plusDays: patch.plusDays ?? (cur.kind === 'LEAD_TIME' ? cur.plusDays : 0) }
                    : kind === 'FIXED'
                      ? { kind: 'FIXED', start: patch.start ?? (cur.kind === 'FIXED' ? cur.start : today), end: patch.end ?? (cur.kind === 'FIXED' ? cur.end : monthEndKey(today)) }
                      : { kind: 'RELATIVE', offsetDays: patch.offsetDays ?? (cur.kind === 'RELATIVE' ? cur.offsetDays : 0), lengthDays: patch.lengthDays ?? (cur.kind === 'RELATIVE' ? cur.lengthDays : 30) };
            return { ...c, periodRule, sentence: sentenceFor(c.anchor, periodRule, c.coverDays, c.shape), fromTemplate: false };
        });
    const toggleObjective = async (o: SavedObjective): Promise<void> => {
        await saveReplenishmentObjective({ id: o.id, sentence: o.sentence, anchor: o.anchor, shape: o.shape, periodRule: o.periodRule, coverDays: o.coverDays ?? null, previewLevels: o.previewLevels, onHome: !o.onHome });
        setTouched(true);
        onObjectivesChanged();
    };
    const removeObjective = async (o: SavedObjective): Promise<void> => {
        await deleteReplenishmentObjective(o.id);
        setTouched(true);
        onObjectivesChanged();
    };

    const setAllotment = (patch: Partial<ReplenishmentPolicy['allotmentPolicy']>): void => {
        setDraft((d) => ({
            ...d,
            allotmentPolicy: { priority: eff.allotmentPolicy?.priority ?? 'NEED_BY', tieBreak: eff.allotmentPolicy?.tieBreak ?? 'QTY', pinned: eff.allotmentPolicy?.pinned ?? [], byChannel: eff.allotmentPolicy?.byChannel === true, ...patch },
        }));
    };
    const routeColumns = useMemo<DataSheetColumn<RouteChoice>[]>(
        () => [
            { key: 'sku', header: 'SKU', width: 220, sticky: true, render: (_v, r): ReactElement => <Stack gap={0}><Text size="sm" fw={600}>{r.sku}</Text><Text size="xs" c="dimmed" truncate>{r.name ?? ''}</Text></Stack> },
            { key: 'options', header: 'Can be', width: 420, render: (_v, r): ReactElement => <Stack gap={0}>{r.options.map((o) => <Text key={o.route} size="xs">{ROUTE_LABEL[o.route]} · {o.detail}{o.leadDays != null ? ` · ${o.leadDays}d` : ''}</Text>)}</Stack> },
            { key: 'route', header: 'Source it by', width: 180, render: (_v, r): ReactElement => <Select size="xs" placeholder="pick one" value={sourcingDraft.routes?.[r.productId] ?? r.current} data={r.options.map((o) => ({ value: o.route, label: ROUTE_LABEL[o.route] }))} onChange={(v) => v && setRoute(r.productId, v as SourcingRoute)} /> },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sourcingDraft],
    );
    const sourceColumns = useMemo<DataSheetColumn<SourceChoice>[]>(
        () => [
            { key: 'sku', header: 'Product', width: 220, sticky: true, render: (_v, r): ReactElement => <Stack gap={0}><Text size="sm" fw={600}>{r.sku}</Text><Text size="xs" c="dimmed" truncate>{r.madeFor.length ? `SFG for ${r.madeFor.join(', ')}` : (r.name ?? '')}</Text></Stack> },
            { key: 'kind', header: 'Who', width: 110, render: (_v, r): ReactElement => <Text size="xs">{r.kind === 'FACTORY' ? 'Factory' : 'FG vendor'}</Text> },
            { key: 'pick', header: 'Use', width: 480, render: (_v, r): ReactElement => <SourcePicker options={r.options} current={sourcingDraft.factories?.[r.productId] ?? r.current} onChange={(rule) => setFactory(r.productId, rule)} /> },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sourcingDraft],
    );
    const materialColumns = useMemo<DataSheetColumn<MaterialChoice>[]>(
        () => [
            { key: 'name', header: 'Material', width: 260, sticky: true, render: (_v, r): ReactElement => <Stack gap={0}><Text size="sm" fw={600} truncate>{r.name ?? r.materialId}</Text><Text size="xs" c="dimmed">{r.materialId}{r.unit ? ` · ${r.unit}` : ''}</Text></Stack> },
            { key: 'usedBy', header: 'In recipes', width: 90, align: 'right', render: (_v, r): ReactElement => <Text size="sm">{r.usedBy}</Text> },
            { key: 'pick', header: 'Supplier', width: 480, render: (_v, r): ReactElement => <SourcePicker options={r.options} current={sourcingDraft.vendors?.[r.materialId] ?? r.current} onChange={(rule) => setVendor(r.materialId, rule)} /> },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sourcingDraft],
    );

    if (!opened) return null;
    const onPage = objectives.filter((o) => o.onHome);
    const unanswered = sourcing
        ? sourcing.routes.filter((r) => !(sourcingDraft.routes?.[r.productId] ?? r.current)).length + sourcing.sources.filter((r) => !(sourcingDraft.factories?.[r.productId] ?? r.current)).length + sourcing.materials.filter((r) => !(sourcingDraft.vendors?.[r.materialId] ?? r.current)).length
        : 0;

    return createPortal(
        <RemoveScroll forwardProps removeScrollBar={false}>
            <div className={classes.backdrop} onClick={() => void close()} role="presentation">
                <div className={classes.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Replenishment setup">
                    <div className={classes.head}>
                        <Stack gap={0}>
                            <Text fw={700} size="lg">
                                Set up replenishment
                            </Text>
                            <Text size="xs" c="dimmed">
                                Six steps. Each one saves when you continue, and the engine runs again when you leave, so the page always reflects the settings.
                            </Text>
                        </Stack>
                        <ActionIcon variant="subtle" color="gray" onClick={() => void close()} aria-label="Close">
                            <X size={18} />
                        </ActionIcon>
                    </div>
                    <div className={classes.body}>
                        <Stepper active={step} onStepClick={(i) => void goTo(i)} size="sm" allowNextStepsSelect>
                            <Stepper.Step label="Levels" description="Safety stock and refill">
                                <Stack gap="lg" mt="md">
                                    <Field label="Safety stock (L)" hint="The cover a SKU may fall to before the engine acts. The brand default, then per tier.">
                                        <LevelEditor value={brandLevel} fallbackDays={fallbackDays} onChange={(v) => setDraft((d) => ({ ...d, levelPolicy: { ...(eff.levelPolicy ?? {}), brand: v ?? { kind: 'DAYS', days: fallbackDays } } }))} />
                                        <TierRows policy={eff.levelPolicy ?? { brand: brandLevel }} fallbackDays={fallbackDays} tiers={tiers} onChange={(p) => setDraft((d) => ({ ...d, levelPolicy: p }))} />
                                    </Field>
                                    <Field label="Replenish up to (R)" hint={`The cover a replenishment restores. Left unset it is L plus the run cadence (${runEvery} day${runEvery === 1 ? '' : 's'}): a landing has to carry the SKU past the next run, else it is a dip again the next morning.`}>
                                        <LevelEditor value={eff.refillPolicy?.brand ?? null} fallbackDays={fallbackDays} allowSame onChange={(v) => setDraft((d) => ({ ...d, refillPolicy: v ? { ...(eff.refillPolicy ?? {}), brand: v } : null }))} />
                                        {eff.refillPolicy && <TierRows policy={eff.refillPolicy} fallbackDays={fallbackDays} tiers={tiers} onChange={(p) => setDraft((d) => ({ ...d, refillPolicy: p }))} />}
                                    </Field>
                                    {eff.refillPolicy == null && (
                                        <Text size="xs" c="dimmed">
                                            R is L + {runEvery} day{runEvery === 1 ? '' : 's'} right now: one run per cadence, each sized to the days between runs. Set R higher to order in fewer, larger runs.
                                        </Text>
                                    )}
                                </Stack>
                            </Stepper.Step>

                            <Stepper.Step label="Sourcing" description="Where there is a choice">
                                <Stack gap="md" mt="md">
                                    <Text size="xs" c="dimmed">
                                        Only what master data leaves open: an FG that can be made, made as its SFG or bought; a product more than one factory or FG vendor can supply; a material more than one vendor supplies. Answered once, read on every run. A pin on one order still overrides it for that order.
                                    </Text>
                                    <Field label="When an item has more than one source and no answer below" hint="Mapped and answered items are never asked. “I choose on every order” dates each order at the longest lead among its vendors, the earliest it would have to start, and waits for your pick; the pick moves the date, not the quantity.">
                                        <Select size="xs" w={300} value={eff.defaultChoiceRule ?? 'SHORTEST_LEAD'} data={CHOICE_RULE_OPTIONS} onChange={(v) => v && setDraft((d) => ({ ...d, defaultChoiceRule: v as ReplenishmentPolicy['defaultChoiceRule'] }))} />
                                    </Field>
                                    {loadingSourcing && <Loader size="xs" />}
                                    {sourcing && (
                                        <>
                                            {unanswered > 0 && (
                                                <Text size="sm" c="orange">
                                                    {unanswered} still open. Until answered, the engine {eff.defaultChoiceRule === 'ASK' ? 'dates each order at the longest lead and asks you on the order' : eff.defaultChoiceRule === 'SPLIT_BY_SHARE' ? 'splits evenly' : 'takes the shortest lead'}.
                                                </Text>
                                            )}
                                            <Field label={`Make, make as SFG, or buy (${sourcing.routes.length})`} hint="FGs with more than one way to source them. The rest have exactly one and are not asked.">
                                                {sourcing.routes.length === 0 ? <Text size="sm" c="dimmed">Every FG has exactly one route.</Text> : <DataSheet<RouteChoice> data={sourcing.routes} columns={routeColumns} getRowKey={(r) => r.productId} rowHeight={48} bare hideHeaderBar maxHeight={300} />}
                                            </Field>
                                            <Field label={`Which factory or FG vendor (${sourcing.sources.length})`} hint="For the product actually made or bought under the route above.">
                                                {sourcing.sources.length === 0 ? <Text size="sm" c="dimmed">Every product has exactly one.</Text> : <DataSheet<SourceChoice> data={sourcing.sources} columns={sourceColumns} getRowKey={(r) => r.productId} rowHeight={44} bare hideHeaderBar maxHeight={300} />}
                                            </Field>
                                            <Field label={`Which material supplier (${sourcing.materials.length})`} hint="Materials in the chosen recipes that more than one vendor supplies.">
                                                {sourcing.materials.length === 0 ? <Text size="sm" c="dimmed">Every material has exactly one supplier.</Text> : <DataSheet<MaterialChoice> data={sourcing.materials} columns={materialColumns} getRowKey={(r) => r.materialId} rowHeight={44} bare hideHeaderBar maxHeight={300} />}
                                            </Field>
                                            {sourcing.unmapped.length > 0 && (
                                                <Alert color="yellow" title={`${sourcing.unmapped.length} materials have no supplier mapped`}>
                                                    <Text size="xs">
                                                        Not a choice: missing master data. The engine plans them at a 7-day lead and flags every order as a choice pending. Map a vendor under Materials → vendor mappings. {sourcing.unmapped.slice(0, 8).map((m) => m.name ?? m.materialId).join(' · ')}{sourcing.unmapped.length > 8 ? ' …' : ''}
                                                    </Text>
                                                </Alert>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            </Stepper.Step>

                            <Stepper.Step label="Objectives" description="What the page answers">
                                <Stack gap="md" mt="md">
                                    <Text size="xs" c="dimmed">
                                        Each objective is a tab on the page and decides how far the engine looks. A SKU-level "inside their lead time" window means every SKU that must be acted on now.
                                    </Text>
                                    <Stack gap={6}>
                                        {objectives.length === 0 && (
                                            <Text size="sm" c="dimmed">
                                                None yet. Pick a template or write one.
                                            </Text>
                                        )}
                                        {objectives.map((o) => (
                                            <Group key={o.id} className={`${classes.row} ${composer?.id === o.id ? classes.rowActive : ''}`} justify="space-between" wrap="nowrap">
                                                <Stack gap={0} className={classes.grow} role="button" tabIndex={0} onClick={() => composeFromSaved(o)} title="Open in the composer to change its window or anchor">
                                                    <Text size="sm" fw={600} truncate>
                                                        {o.sentence}
                                                    </Text>
                                                    <Text size="xs" c="dimmed" truncate>
                                                        {ANCHOR_LABEL[o.anchor]} · {describePeriodRule(o.periodRule)} · click to edit
                                                    </Text>
                                                </Stack>
                                                <Switch size="xs" checked={o.onHome} onChange={() => void toggleObjective(o)} label="on page" />
                                                <ActionIcon variant="subtle" color="gray" onClick={() => void removeObjective(o)} aria-label="Remove objective">
                                                    <Trash size={14} />
                                                </ActionIcon>
                                            </Group>
                                        ))}
                                    </Stack>
                                    <Field label="Templates" hint="Click one to see what it means; it is added only when you confirm below.">
                                        <div className={classes.templates}>
                                            {OBJECTIVE_TEMPLATES.filter((t) => !objectives.some((o) => o.sentence === t.sentence)).map((t) => (
                                                <button key={t.key} type="button" className={`${classes.template} ${composer?.fromTemplate && composer.sentence === t.sentence ? classes.templateActive : ''}`} onClick={() => composeFromTemplate(t)}>
                                                    <Text size="sm" fw={600}>
                                                        {t.label}
                                                    </Text>
                                                    <Text size="xs" c="dimmed">
                                                        {ANCHOR_LABEL[t.anchor]} · {describePeriodRule(t.periodRule)}
                                                    </Text>
                                                </button>
                                            ))}
                                        </div>
                                    </Field>
                                    <Field label="Or write your own" hint="The sentence is read into the same variables; correct them below if the reading is wrong.">
                                        <TextInput size="xs" placeholder="e.g. RM / PM to order for December, or Production for the next 2 weeks" value={composer?.fromTemplate ? '' : composer?.sentence ?? ''} onChange={(e) => composeFromSentence(e.currentTarget.value)} />
                                    </Field>
                                    {composer && (
                                        <div className={classes.breakdown}>
                                            <Text size="sm" fw={700}>
                                                “{composer.sentence}”
                                            </Text>
                                            <div className={classes.vars}>
                                                <Text size="xs" c="dimmed">Shows</Text>
                                                {composer.anchor === 'DIP' ? (
                                                    <Group gap="xs" align="center" wrap="wrap">
                                                        <Select size="xs" w={200} value={composer.shape} data={(Object.keys(SHAPE_LABEL) as ObjectiveShape[]).map((k) => ({ value: k, label: SHAPE_LABEL[k].split(':')[0] }))} onChange={(v) => v && setComposerShape(v as ObjectiveShape)} />
                                                        <Text size="sm">{composer.shape === 'SKU' ? SHAPE_LABEL.SKU : `${SHAPE_LABEL[composer.shape].split(': ')[1]} — the documents behind the dips in the window, whatever their own dates`}</Text>
                                                    </Group>
                                                ) : (
                                                    <Text size="sm">{SHAPE_LABEL[SHAPE_FOR_ANCHOR[composer.anchor]]}</Text>
                                                )}

                                                <Text size="xs" c="dimmed">Picks rows by</Text>
                                                <Group gap="xs" align="center" wrap="wrap">
                                                    <Select size="xs" w={260} value={composer.anchor} data={(Object.keys(ANCHOR_LABEL) as ObjectiveAnchor[]).map((a) => ({ value: a, label: ANCHOR_LABEL[a] }))} onChange={(v) => v && setComposerAnchor(v as ObjectiveAnchor)} />
                                                    <Text size="sm">{ANCHOR_MEANING[composer.anchor]}</Text>
                                                </Group>

                                                <Text size="xs" c="dimmed">Window</Text>
                                                <Group gap="xs" align="center" wrap="wrap">
                                                    <SegmentedControl size="xs" value={composer.periodRule.kind} onChange={(v) => setComposerRule({ kind: v as 'LEAD_TIME' | 'RELATIVE' | 'FIXED' })} data={[{ value: 'RELATIVE', label: 'Next N days' }, { value: 'FIXED', label: 'Between dates' }, { value: 'LEAD_TIME', label: "Inside each SKU's lead time" }]} />
                                                    {composer.periodRule.kind === 'RELATIVE' && (
                                                        <>
                                                            <NumberInput size="xs" w={120} min={0} value={composer.periodRule.offsetDays} onChange={(v) => setComposerRule({ offsetDays: Number(v) || 0 })} prefix="from +" suffix="d" />
                                                            <NumberInput size="xs" w={110} min={1} value={composer.periodRule.lengthDays} onChange={(v) => setComposerRule({ lengthDays: Number(v) || 1 })} suffix=" days" />
                                                        </>
                                                    )}
                                                    {composer.periodRule.kind === 'LEAD_TIME' && <NumberInput size="xs" w={130} min={0} value={composer.periodRule.plusDays} onChange={(v) => setComposerRule({ plusDays: Number(v) || 0 })} prefix="+ " suffix=" days" />}
                                                    {composer.periodRule.kind === 'FIXED' && (
                                                        <>
                                                            <DatePickerInput
                                                                size="xs"
                                                                w={240}
                                                                type="range"
                                                                allowSingleDateInRange
                                                                valueFormat="D MMM YYYY"
                                                                value={[composer.periodRule.start, composer.periodRule.end]}
                                                                onChange={([a, b]) => {
                                                                    if (a && b) setComposerRule({ start: a, end: b });
                                                                }}
                                                            />
                                                            <Select size="xs" w={170} placeholder="or a whole month" value={composer.periodRule.start.endsWith('-01') && composer.periodRule.end === monthEndKey(composer.periodRule.start) ? composer.periodRule.start : null} data={monthOptions(localDateKey())} onChange={(v) => v && setComposerRule({ start: v, end: monthEndKey(v) })} />
                                                        </>
                                                    )}
                                                    <Text size="sm">
                                                        {(() => {
                                                            const w = windowOf(composer.periodRule);
                                                            return w.perSku ? `from today to today + each SKU's longest lead${composer.periodRule.kind === 'LEAD_TIME' && composer.periodRule.plusDays ? ` + ${composer.periodRule.plusDays}d` : ''}` : `${dayLabel(w.start)} → ${dayLabel(w.end)}`;
                                                        })()}
                                                    </Text>
                                                </Group>

                                                <Text size="xs" c="dimmed">Safety stock (L)</Text>
                                                <Text size="sm">{describeLevel(eff.levelPolicy?.brand, `${fallbackDays} days (legacy default)`)}{eff.levelPolicy?.tiers && Object.keys(eff.levelPolicy.tiers).length ? ` · tier overrides on ${Object.keys(eff.levelPolicy.tiers).join(', ')}` : ''} — from step 1, not the sentence</Text>

                                                <Text size="xs" c="dimmed">Replenish to (R)</Text>
                                                <Text size="sm">{describeLevel(eff.refillPolicy?.brand, `L + ${runEvery} day${runEvery === 1 ? '' : 's'} (the run cadence)`)} — from step 1</Text>
                                                {composer.anchor === 'MATERIAL_PLACE_BY' && (
                                                    <>
                                                        <Text size="xs" c="dimmed">Each material order covers</Text>
                                                        <Group gap="xs" align="center">
                                                            <NumberInput size="xs" w={140} min={0} value={composer.coverDays ?? 0} onChange={(v) => setComposerCover(Math.max(0, Number(v) || 0) || null)} suffix=" days" />
                                                            <Text size="sm">of production from its landing: every run of every SKU using the material at that factory inside that window goes on one order. 0 = one order per landing day.</Text>
                                                        </Group>
                                                    </>
                                                )}

                                                <Text size="xs" c="dimmed">Engine looks ahead</Text>
                                                <Text size="sm">
                                                    {(() => {
                                                        const h = horizonEstimate(composer.periodRule, composer.anchor, eff.horizonDays ?? 365, eff.maxLeadDays ?? 0, eff.maxRefillDays ?? 0, undefined, composer.coverDays ?? 0);
                                                        return `Each SKU is planned only up to its own window end. The curve is loaded further so a dip at the edge can be landed and sized: about ${h.days} days for the longest chain (${h.parts.join(', ')}). The run takes the furthest across objectives.`;
                                                    })()}
                                                </Text>

                                                {composer.cues.length > 0 && (
                                                    <>
                                                        <Text size="xs" c="dimmed">Read as</Text>
                                                        <Text size="xs" c="dimmed">{composer.cues.join(' · ')}</Text>
                                                    </>
                                                )}
                                            </div>
                                            <Group gap="xs" mt="xs">
                                                <Button size="xs" onClick={() => void addObjective(composer)}>
                                                    {composer.id ? 'Save changes' : 'Add this objective'}
                                                </Button>
                                                <Button size="xs" variant="subtle" onClick={() => setComposer(null)}>
                                                    Clear
                                                </Button>
                                            </Group>
                                        </div>
                                    )}
                                </Stack>
                            </Stepper.Step>

                            <Stepper.Step label="Allotment" description="Who gets scarce material">
                                <Stack gap="md" mt="md">
                                    <Text size="xs" c="dimmed">
                                        When a material or a factory cannot serve every run, the engine hands it out in this order. Pinned SKUs always come first.
                                    </Text>
                                    <Group gap="md" wrap="wrap">
                                        <Select
                                            size="xs"
                                            label="First"
                                            w={260}
                                            value={eff.allotmentPolicy?.priority ?? 'NEED_BY'}
                                            data={[{ value: 'NEED_BY', label: 'Earliest dip first' }, { value: 'QTY', label: 'Largest run first' }, { value: 'DRR', label: 'Fastest seller first' }, { value: 'DOH', label: 'Thinnest cover first' }]}
                                            onChange={(v) => v && setAllotment({ priority: v as ReplenishmentPolicy['allotmentPolicy']['priority'] })}
                                        />
                                        <Select
                                            size="xs"
                                            label="Then, on a tie"
                                            w={260}
                                            value={eff.allotmentPolicy?.tieBreak ?? 'QTY'}
                                            data={[{ value: 'QTY', label: 'Largest run' }, { value: 'SMALLEST', label: 'Smallest run (protect small SKUs)' }, { value: 'DRR', label: 'Fastest seller' }, { value: 'DOH', label: 'Thinnest cover' }]}
                                            onChange={(v) => v && setAllotment({ tieBreak: v as ReplenishmentPolicy['allotmentPolicy']['tieBreak'] })}
                                        />
                                    </Group>
                                    <TagsInput
                                        size="xs"
                                        label="Pinned SKUs"
                                        description="Product ids that always come first, in this order. Type an id and press Enter."
                                        value={eff.allotmentPolicy?.pinned ?? []}
                                        onChange={(v) => setAllotment({ pinned: v })}
                                    />
                                    <Switch
                                        size="xs"
                                        label="Serve channels by priority"
                                        description="Each run is split by the priority of the channels its demand comes from (set under Demand). The factory's day goes to every SKU's priority-1 share first, then priority 2, then the unranked rest; inside a tier it is shared in proportion. The order above then settles material inside a tier."
                                        checked={eff.allotmentPolicy?.byChannel === true}
                                        onChange={(e) => setAllotment({ byChannel: e.currentTarget.checked })}
                                    />
                                </Stack>
                            </Stepper.Step>

                            <Stepper.Step label="Demand" description="The demand workbook">
                                <DemandWorkbook
                                    demand={demand}
                                    preview={preview}
                                    previewing={previewing}
                                    onChange={(p) => {
                                        setDemand(p);
                                        void refreshPreview(p);
                                    }}
                                />
                            </Stepper.Step>

                            <Stepper.Step label="Run" description="How often, and now">
                                <Stack gap="md" mt="md">
                                    <Switch size="sm" label="Run the engine after the daily sync" checked={eff.runAfterSync ?? true} onChange={(e) => setDraft((d) => ({ ...d, runAfterSync: e.currentTarget.checked }))} />
                                    <NumberInput size="xs" label="Every" description="1 = after every sync; 7 = weekly. Nothing is acted on between runs, so this is also the least gap between two replenishments of one SKU." w={220} min={1} disabled={!(eff.runAfterSync ?? true)} value={eff.runEveryDays ?? 1} onChange={(v) => setDraft((d) => ({ ...d, runEveryDays: Math.max(1, Number(v) || 1) }))} suffix=" days" />
                                    <NumberInput size="xs" label="Furthest the engine may look" description="Objectives set the window; this caps it" w={220} min={30} value={eff.horizonDays ?? 365} onChange={(v) => setDraft((d) => ({ ...d, horizonDays: Number(v) || 365 }))} suffix=" days" />
                                    <Text size="sm">
                                        {onPage.length === 0 ? 'Add at least one objective on step 3 before running.' : `${onPage.length} objective${onPage.length === 1 ? '' : 's'} on the page. Run now to see them filled; from tomorrow it runs by itself.`}
                                    </Text>
                                </Stack>
                            </Stepper.Step>
                        </Stepper>
                        {error && (
                            <Alert color="red" mt="md" withCloseButton onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                    </div>
                    <div className={classes.foot}>
                        <Button variant="subtle" size="xs" onClick={() => void goTo(Math.max(0, step - 1))} disabled={step === 0}>
                            Back
                        </Button>
                        <Group gap="xs">
                            {step < 5 ? (
                                <Button size="xs" rightSection={<ArrowRight size={14} />} onClick={() => void next()} loading={saving} disabled={step === 2 && onPage.length === 0}>
                                    {(step === 1 ? sourcingDirty : step === 4 ? false : dirty) ? 'Save and continue' : 'Continue'}
                                </Button>
                            ) : (
                                <Button size="xs" onClick={() => void runNow()} loading={running} disabled={onPage.length === 0}>
                                    Save and run now
                                </Button>
                            )}
                        </Group>
                    </div>
                </div>
            </div>
        </RemoveScroll>,
        document.body,
    );
};

export default SetupWizard;
